import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";

/**
 * TechnicianDetail - Detailed technician profile page
 *
 * Features:
 * - Full bio and profile picture
 * - Portfolio gallery (recent completed designs)
 * - Reviews list with customer names and ratings
 * - Average rating display
 * - Specialties
 * - Booking CTA button
 */
export function TechnicianDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [technician, setTechnician] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadTechnician();
  }, [id]);

  async function loadTechnician() {
    try {
      const response = await fetch(`/api/public/technicians/${id}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Technician not found");
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setTechnician(data.technician);
    } catch (err) {
      setError(err.message || "Failed to load technician");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "var(--text-muted)" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
          <div>Loading technician profile...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "30px 16px" }}>
          <Card style={{ maxWidth: "100%", margin: 0 }}>
            <div style={{ textAlign: "center", color: "#C02020" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{error}</div>
              <button
                onClick={() => navigate("/technicians")}
                style={{
                  marginTop: 16,
                  padding: "10px 20px",
                  borderRadius: 8,
                  fontWeight: 600,
                  background: "var(--bg-elevated)",
                  color: "var(--text)",
                  border: "1px solid var(--border)",
                  cursor: "pointer"
                }}
              >
                ← Back to Technicians
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!technician) return null;

  const avgRating = technician.avg_rating || 0;
  const reviewCount = technician.reviews?.length || 0;
  const portfolio = technician.portfolio || [];

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "30px 16px" }}>
        {/* Back Button */}
        <button
          onClick={() => navigate("/technicians")}
          style={{
            marginBottom: 20,
            padding: "8px 16px",
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 13,
            background: "var(--bg-elevated)",
            color: "var(--text-muted)",
            border: "1px solid var(--border)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6
          }}
        >
          ← Back to Technicians
        </button>

        {/* Profile Header */}
        <Card style={{ maxWidth: "100%", margin: 0, marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Avatar */}
            <div style={{ flexShrink: 0 }}>
              {technician.avatar_url ? (
                <img
                  src={technician.avatar_url}
                  alt={technician.full_name}
                  style={{
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid var(--border)"
                  }}
                />
              ) : (
                <div style={{
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 56,
                  border: "4px solid var(--border)"
                }}>
                  {(technician.full_name || "?")[0].toUpperCase()}
                </div>
              )}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{
                fontFamily: "var(--font-display)",
                fontSize: 32,
                fontWeight: 700,
                color: "var(--text)",
                marginTop: 0,
                marginBottom: 12
              }}>
                {technician.full_name}
              </h1>

              {/* Rating */}
              {avgRating > 0 && (
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 16px",
                  background: "linear-gradient(135deg, rgba(196,135,154,0.12), rgba(196,135,154,0.06))",
                  borderRadius: 999,
                  marginBottom: 16
                }}>
                  <span style={{ fontSize: 20, fontWeight: 700, color: "#FFB800" }}>
                    ⭐ {avgRating.toFixed(1)}
                  </span>
                  <span style={{ fontSize: 14, color: "var(--text-muted)" }}>
                    ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})
                  </span>
                </div>
              )}

              {/* Bio */}
              {technician.bio && (
                <p style={{
                  fontSize: 16,
                  color: "var(--text)",
                  lineHeight: 1.6,
                  marginBottom: 16
                }}>
                  {technician.bio}
                </p>
              )}

              {/* Specialties */}
              {technician.specialties && technician.specialties.length > 0 && (
                <div>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: 8
                  }}>
                    Specialties
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {technician.specialties.map((spec, i) => (
                      <span key={i} style={{
                        fontSize: 13,
                        fontWeight: 600,
                        padding: "6px 14px",
                        borderRadius: 999,
                        background: "rgba(196,135,154,0.12)",
                        color: "var(--mauve)",
                        border: "1px solid rgba(196,135,154,0.2)"
                      }}>
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Book CTA */}
              <div style={{ marginTop: 20 }}>
                <Button
                  label="Book with this Technician"
                  icon="📅"
                  onClick={() => navigate("/booking")}
                  style={{ fontSize: 15, padding: "12px 24px" }}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Portfolio Section */}
        {portfolio.length > 0 && (
          <Card style={{ maxWidth: "100%", margin: 0, marginBottom: 20 }}>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: 24,
              fontWeight: 700,
              color: "var(--text)",
              marginTop: 0,
              marginBottom: 16
            }}>
              Portfolio
            </h2>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 200px), 1fr))",
              gap: 12
            }}>
              {portfolio.map((design, i) => (
                <div key={i} style={{
                  aspectRatio: "1",
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "var(--bg-soft)",
                  border: "1px solid var(--border)",
                  position: "relative"
                }}>
                  {design.image_url ? (
                    <img
                      src={design.image_url}
                      alt={design.name || "Design"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  ) : (
                    <div style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 48
                    }}>
                      💅
                    </div>
                  )}
                  {design.name && (
                    <div style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: 8,
                      background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                      {design.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Reviews Section */}
        {technician.reviews && technician.reviews.length > 0 && (
          <Card style={{ maxWidth: "100%", margin: 0 }}>
            <h2 style={{
              fontFamily: "var(--font-display)",
              fontSize: 24,
              fontWeight: 700,
              color: "var(--text)",
              marginTop: 0,
              marginBottom: 16
            }}>
              Reviews
            </h2>
            <div style={{ display: "grid", gap: 16 }}>
              {technician.reviews.map((review, i) => (
                <div key={i} style={{
                  padding: 16,
                  background: "var(--bg-soft)",
                  borderRadius: 12,
                  border: "1px solid var(--border)"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 12
                  }}>
                    <div>
                      <div style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--text)",
                        marginBottom: 4
                      }}>
                        {review.customer?.full_name || "Customer"}
                      </div>
                      <div style={{
                        fontSize: 11,
                        color: "var(--text-muted)"
                      }}>
                        {new Date(review.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 16,
                      color: "#FFB800"
                    }}>
                      {"⭐".repeat(review.rating)}
                    </div>
                  </div>
                  {review.comment && (
                    <p style={{
                      fontSize: 14,
                      color: "var(--text)",
                      lineHeight: 1.6,
                      margin: 0
                    }}>
                      {review.comment}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* No Reviews Message */}
        {(!technician.reviews || technician.reviews.length === 0) && (
          <Card style={{ maxWidth: "100%", margin: 0 }}>
            <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⭐</div>
              <div>No reviews yet. Be the first to book and leave a review!</div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
